import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';

import { useCitySelectedContext } from '../../../../contexts/city-context/CityContext';
import { DownloadApp } from '../../../../shared/download-app/domain/entities/DownloadApp';
import { NextImage } from '../../../../shared/types/CommonVM';
import NextImageV2 from '../../../../ui/components/NextImageV2';
import {
  AuthUser,
  useAuthContext,
} from '../../../login/view-model/contexts/auth-context/AuthContextV1';
import * as DownloadAnalyticsHelper from '../../helpers/DownloadAnalyticsHelper.mjs';
import styles from '../styles/DownloadAppCard.module.scss';

// Bridges the ReScript DownloadAnalyticsHelper until it is migrated to TS.
const getAppDownloadLinkSection =
  DownloadAnalyticsHelper.getAppDownloadLinkSection as (
    queryParams: Record<string, string>,
    city: string | undefined,
    isDesktop: boolean,
    currentPageRoute: string,
  ) => string;

const publishDownloadAppCardEvent =
  DownloadAnalyticsHelper.publishDownloadAppCardEvent as (
    category: string,
    user: AuthUser | undefined,
  ) => void;

function getURLParams(searchStr: string): Record<string, string> {
  const urlParams: Record<string, string> = {};
  searchStr.split('&').forEach(param => {
    const parts = param.split('=');
    if (parts.length >= 2) {
      urlParams[parts[0]] = parts[1];
    }
  });
  return urlParams;
}

interface DownloadAppCardCmpProps {
  data: DownloadApp;
  image: NextImage;
}

function DownloadAppCardCmp({
  data,
  image,
}: Readonly<DownloadAppCardCmpProps>): React.ReactElement {
  const router = useRouter();
  const { city_selected, category } = useCitySelectedContext();
  const { user } = useAuthContext();

  const download_link = useMemo(
    () =>
      getAppDownloadLinkSection(
        getURLParams(router.asPath),
        city_selected,
        false,
        router.pathname,
      ),
    [router.asPath, router.pathname, city_selected],
  );

  const handleDownloadClick = useCallback((): void => {
    publishDownloadAppCardEvent(category, user);
    globalThis.location.href = download_link;
  }, [category, user, download_link]);

  return (
    <div className={styles['card']}>
      <div className={styles['text']}>{data.text}</div>
      <button
        type="button"
        className={styles['download-btn']}
        onClick={handleDownloadClick}
      >
        {data.cta_text}
      </button>
      <div className={styles['image']}>
        <NextImageV2
          src={image.src}
          alt={image.alt}
          width={image.width}
          height={image.height}
        />
      </div>
    </div>
  );
}

export default DownloadAppCardCmp;
